import React, { FC, useCallback, useMemo, useState } from "react";
import { Dimensions, LayoutChangeEvent } from "react-native";
import {
  Box,
  Image,
  Layout,
  Overlay,
  SafeAreaView,
  ScrollView,
  View,
  useDevice,
  ALIGN,
  POINTER,
  SIZE,
  POSITION,
} from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { ButtonIcon, Text } from "@lookiero/sty-psp-ui";
import { TouchableWithoutFeedback } from "../../../../components/touchableWithoutFeedback/TouchableWithoutFeedback";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Media } from "../../../../shared/types";
import { style as sliderPreviewModalStyle, THUMBNAIL_WIDTH } from "./SliderPreviewModal.styles";

const IMAGE_ASPECT_RATIO = 0.666; // 2/3 -> 4/5
const IMAGE_MAX_WIDTH = 1800;
const HEADER_HEIGHT = 40;
const zoomScale = 2;

interface SliderPreviewModalProps {
  readonly isVisible: boolean;
  readonly images: Media[];
  readonly selectedIndexImage?: number;
  readonly onClose: () => void;
}

const SliderPreviewModal: FC<SliderPreviewModalProps> = ({ isVisible, images, selectedIndexImage, onClose }) => {
  const { screen } = useDevice();
  const { width: windowWidth, height: windowHeight } = Dimensions.get("window");
  const isLandscape = windowWidth >= windowHeight && !screen.L;
  const [imageContainerSize, setImageContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [currentImage, setCurrentImage] = useState<number>(selectedIndexImage || 1);
  const [zoom, setZoom] = useState<boolean>(true);

  const imagesUrl = images.map((image) => image.url);

  // Image container size when the zoom is activated
  const zoomContainerWidth = useMemo(() => {
    let smallResolutionWidth = Math.round(windowHeight * IMAGE_ASPECT_RATIO);
    if (smallResolutionWidth < windowWidth) {
      smallResolutionWidth = windowWidth;
    }

    return screen.L ? windowWidth : smallResolutionWidth;
  }, [screen.L, windowHeight, windowWidth]);
  const zoomContainerHeight = useMemo(() => {
    let smallResolutionHeight = windowHeight;
    if (isLandscape) {
      smallResolutionHeight = Math.round(windowWidth / IMAGE_ASPECT_RATIO);
    }

    return screen.L ? Math.round(windowWidth / IMAGE_ASPECT_RATIO) : smallResolutionHeight;
  }, [isLandscape, screen.L, windowHeight, windowWidth]);

  const totalImages = imagesUrl.length;
  const mainImage = imagesUrl[currentImage - 1];
  const currentZoomScale = zoom ? zoomScale : 1;

  const width = zoom ? zoomContainerWidth : imageContainerSize.width;
  const height = zoom ? zoomContainerHeight : imageContainerSize.height;
  const targetScroll = useMemo(
    () => (width * currentZoomScale - windowWidth) / 2,
    [currentZoomScale, width, windowWidth],
  );

  const nextImage = useCallback(() => {
    if (currentImage < totalImages) {
      setCurrentImage(currentImage + 1);
    }
  }, [currentImage, totalImages]);

  const previousImage = useCallback(() => {
    if (currentImage > 0) {
      setCurrentImage(currentImage - 1);
    }
  }, [currentImage]);

  // When the layout loads I need to know if it is greater than the height of the screen or not
  const onLayoutHandler = (event: LayoutChangeEvent) => {
    const { height: heightLayout } = event.nativeEvent.layout;
    const height = heightLayout < windowHeight ? heightLayout : windowHeight - HEADER_HEIGHT;
    const width = Math.round(height * IMAGE_ASPECT_RATIO);

    setImageContainerSize({ width, height });
  };

  const onPressImage = () => {
    if (!isLandscape) {
      setZoom(!zoom);
    }
  };

  const productModalZoomNumberText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_MODAL_ZOOM_NUMBER,
    values: { position: currentImage, total: totalImages },
  });

  const style = useMemo(() => sliderPreviewModalStyle(), []);

  return (
    <View
      {...(!isVisible
        ? {
            accessibilityElementsHidden: true,
            importantForAccessibility: "no-hide-descendants",
            pointer: POINTER.NONE,
          }
        : undefined)}
      alignItems={screen.S ? ALIGN.END : ALIGN.CENTER}
      marginTop="0"
      position={POSITION.FIXED}
      style={style.modal}
      wide
    >
      <Overlay isVisible={isVisible} onPress={onClose} />
      <Layout layer={SIZE.L} style={style.layout} fullWidth>
        <Box style={style.box}>
          <SafeAreaView style={style.wrapper} wide>
            <View style={[style.header, { justifyContent: screen.L ? "flex-end" : "space-between" }]} wide>
              {!screen.L && (
                <>
                  <View style={style.navigation}>
                    <ButtonIcon
                      accessibilityLabel="previous"
                      name="arrow_left"
                      style={{ button: style.button, icon: [style.icon, currentImage === 1 && style.activeIcon] }}
                      onPress={currentImage === 1 ? undefined : previousImage}
                    />
                    <ButtonIcon
                      accessibilityLabel="next"
                      name="arrow_right"
                      style={{
                        button: style.button,
                        icon: [style.icon, currentImage === totalImages && style.activeIcon],
                      }}
                      onPress={currentImage === totalImages ? undefined : nextImage}
                    />
                  </View>
                  <View style={style.title}>
                    <Text level={3} variant="action">
                      {productModalZoomNumberText}
                    </Text>
                  </View>
                </>
              )}
              <ButtonIcon
                accessibilityLabel="close"
                name="close"
                style={{ button: style.button, icon: style.icon }}
                onPress={onClose}
              />
            </View>
            <ScrollView
              scrollTo={0}
              style={style.content}
              contentContainerStyle={
                screen.L || !zoom
                  ? style.fullHeight
                  : { height: isLandscape ? height * zoomScale : windowHeight * zoomScale }
              }
            >
              {screen.L && (
                <View style={style.imagesThumbnails}>
                  {imagesUrl.map((src, index) => {
                    return (
                      <TouchableWithoutFeedback key={`thumbnail-${index}`} onPress={() => setCurrentImage(index + 1)}>
                        <Image resizeMode="cover" src={`${src}?w=${THUMBNAIL_WIDTH}`} style={style.thumbnail} />
                      </TouchableWithoutFeedback>
                    );
                  })}
                </View>
              )}
              {!screen.L ? (
                <ScrollView
                  contentContainerStyle={{ width: windowWidth * currentZoomScale }}
                  horizontal={true}
                  scrollTo={targetScroll}
                  style={style.content}
                >
                  <View style={style.mainImageContent} onLayout={onLayoutHandler}>
                    <TouchableWithoutFeedback
                      style={[!zoom ? style.mainImageTouchable : undefined, zoom ? style.zoomOut : style.zoomIn]}
                      onPress={onPressImage}
                    >
                      <Image
                        resizeMode="cover"
                        src={mainImage}
                        style={[
                          style.mainImage,
                          {
                            width: width * currentZoomScale,
                            height: height * currentZoomScale,
                            maxWidth: IMAGE_MAX_WIDTH,
                            ...(!zoom && {
                              transform: [
                                {
                                  translateX: -(width * currentZoomScale - windowWidth * currentZoomScale) / 2,
                                },
                              ],
                            }),
                          },
                        ]}
                      />
                    </TouchableWithoutFeedback>
                  </View>
                </ScrollView>
              ) : (
                <View style={style.mainImageContent} onLayout={onLayoutHandler}>
                  <TouchableWithoutFeedback
                    style={[style.mainImageTouchable, zoom ? style.zoomOut : style.zoomIn]}
                    onPress={onPressImage}
                  >
                    <Image
                      resizeMode="cover"
                      src={mainImage}
                      style={[
                        style.mainImage,
                        {
                          width,
                          height,
                          maxWidth: IMAGE_MAX_WIDTH,
                        },
                      ]}
                    />
                  </TouchableWithoutFeedback>
                </View>
              )}
            </ScrollView>
          </SafeAreaView>
        </Box>
      </Layout>
    </View>
  );
};

export { SliderPreviewModal };
